import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject, throwError } from 'rxjs';
import { App_user } from 'src/model/model.app_user';
import { Page } from 'src/model/model.page';
import { tap, catchError, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  basePath = environment.BasePath + "/survey";
  private profilePictureSubject = new BehaviorSubject<{ [userId: number]: string }>({});

  constructor(private httpClient: HttpClient) { }

  private getEtabListHeaders(): HttpHeaders {
    const etabData = sessionStorage.getItem('authUser');
    let etabList = '';
    if (etabData) {
      try {
        const parsedData = JSON.parse(etabData);
        etabList = parsedData?.etablissements?.map((e: any) => e.id).join(',') || '';
      } catch (e) {
        console.error('Error parsing etabList from sessionStorage:', e);
      }
    }
    return new HttpHeaders({
      'etabList': etabList
    });
  }

  getUsers(firstName: string, lastName: string, username: string, page: number, size: number): Observable<Page<App_user>> {
    const url = `${this.basePath}/chercheUser?firstName=${firstName}&lastName=${lastName}&username=${username}&page=${page}&size=${size}`;
    return this.httpClient.get<Page<App_user>>(url, { headers: this.getEtabListHeaders() })
      .pipe(
        tap(response => console.log('Response from getUsers:', response)),
        catchError(error => {
          console.error('Error in getUsers:', error);
          throw error;
        })
      );
  }

  getUsersById(id: number): Observable<App_user> {
    const url = `${this.basePath}/user/${id}`;
    return this.httpClient.get<App_user>(url)
      .pipe(
        tap(response => console.log('Response from getUsersById:', response)),
        catchError(error => {
          console.error('Error in getUsersById:', error);
          throw error;
        })
      );
  }

  saveUser(user: App_user): Observable<App_user> {
    return this.httpClient.post<App_user>(`${this.basePath}/user`, user)
      .pipe(
        catchError(error => {
          console.error('Error in saveUser:', error);
          throw error;
        })
      );
  }

  saveAcount(user: App_user): Observable<App_user> {
    return this.httpClient.post<App_user>(`${this.basePath}/createAcount`, user)
      .pipe(
        catchError(error => {
          console.error('Error in saveAcount:', error);
          throw error;
        })
      );
  }

  editUser(user: App_user): Observable<App_user> {
    return this.httpClient.put<App_user>(`${this.basePath}/user/${user.id}`, user)
      .pipe(
        catchError(error => {
          console.error('Error in editUser:', error);
          throw error;
        })
      );
  }

  deleteUser(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.basePath}/user/${id}`)
      .pipe(
        catchError(error => {
          console.error('Error in deleteUser:', error);
          throw error;
        })
      );
  }

  activerUser(user: App_user): Observable<App_user> {
    return this.httpClient.put<App_user>(`${this.basePath}/user/active`, user)
      .pipe(
        catchError(error => {
          console.error('Error in activerUser:', error);
          throw error;
        })
      );
  }

  desactiverUser(user: App_user): Observable<App_user> {
    return this.httpClient.put<App_user>(`${this.basePath}/user/desactive`, user)
      .pipe(
        catchError(error => {
          console.error('Error in desactiverUser:', error);
          throw error;
        })
      );
  }

  uploadProfilePicture(formData: FormData): Observable<string> {
    const userId = formData.get('userId') as string;
    return this.httpClient.post(
      `${this.basePath}/user/${userId}/profile-picture`,
      formData,
      {

        responseType: 'text' as const
      }
    ).pipe(
      tap(response => {
        console.log('Upload response:', response);
        const currentCache = this.profilePictureSubject.getValue();
        currentCache[+userId] = response;
        this.profilePictureSubject.next(currentCache);
      }),
      catchError(error => {
        console.error('Error in uploadProfilePicture:', error);
        throw error;
      })
    );
  }

  getProfilePicture(userId: number): Observable<string> {
    const cachedPicture = this.profilePictureSubject.getValue()[userId];
    if (cachedPicture) {
      return of(cachedPicture);
    }
    return this.httpClient.get(
      `${this.basePath}/user/${userId}/profile-picture`,
      { responseType: 'text' }
    ).pipe(
      tap(response => {
        console.log('Get profile picture response:', response);
        const currentCache = this.profilePictureSubject.getValue();
        currentCache[userId] = response;
        this.profilePictureSubject.next(currentCache);
      }),
      catchError(error => {
        console.error('Error in getProfilePicture:', error);
        throw error;
      })
    );
  }

  getProfilePictureObservable(): Observable<{ [userId: number]: string }> {
    return this.profilePictureSubject.asObservable();
  }

  // Nouvelle méthode pour forgot-password
  forgotPassword(email: string): Observable<void> {
    const url = `${this.basePath}/forgot-password?email=${email}`;
    return this.httpClient.post<void>(url, null)
      .pipe(
        tap(() => console.log('Forgot password request sent for:', email)),
        catchError(error => {
          console.error('Error in forgotPassword:', error);
          throw error;
        })
      );
  }

  // Nouvelle méthode pour reset-password
  resetPassword(token: string, newPassword: string): Observable<App_user> {
    // Encode les paramètres d'URL pour gérer les caractères spéciaux
    const encodedToken = encodeURIComponent(token);
    const encodedPassword = encodeURIComponent(newPassword);
    const url = `${this.basePath}/reset-password-user?token=${encodedToken}&newPassword=${encodedPassword}`;
    
    return this.httpClient.put<App_user>(url, null)
      .pipe(
        tap(response => console.log('Password reset successful:', response)),
        catchError(error => {
          console.error('Error in resetPassword:', error);
          throw error;
        })
      );
  }

  getPersonnelIdByUserId(userId: number): Observable<number | null> {
    const url = `${this.basePath}/user/${userId}/personnel-id`;
    return this.httpClient.get<number | null>(url)
      .pipe(
        tap(response => console.log(`Personnel ID for user ${userId}:`, response)),
        catchError(error => {
          console.error('Error in getPersonnelIdByUserId:', error);
          throw error;
        })
      );
  }
  register(user: any): Observable<App_user> {
    return this.httpClient.post<App_user>(`${this.basePath}/createAcount`, user);
  }

  changePassword(userId: number, body: { currentPassword: string; newPassword: string }): Observable<void> {
    return this.httpClient.put<void>(`${this.basePath}/user/${userId}/change-password`, body);
  }

  mfaEnable(userId: number, password: string): Observable<void> {
    return this.httpClient.post<void>(`${this.basePath}/user/${userId}/mfa/enable`, { password });
  }

  mfaDisable(userId: number, password: string): Observable<void> {
    return this.httpClient.post<void>(`${this.basePath}/user/${userId}/mfa/disable`, { password });
  }
}
